import { NonModal, useDataMutation } from "@lyfegen-platform/react-common";
import { distinct } from "@lyfegen-platform/utils";
import { Box, TextField, Typography, useTheme } from "@material-ui/core";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import { Formik } from "formik";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Prompt } from "react-router";
import { useApolloClient } from "@apollo/client";
import { useRecoilValue, useSetRecoilState } from "recoil";
import * as Yup from "yup";
import { useDataQuery } from "../../hooks/useDataModule";
import { FlexFinancialDataPoint, Refund } from "../../models/generated/graphql";
import { SnackBarMessageType } from "../../models/snackbarMessageType";
import { adjustmentWindowPosition, snackbarState } from "../../store/layout";
import renderCurrency from "../../utils/currency";
import { isEmptyValue } from "../../utils/is-empty-value";
import { TrFun } from "../../utils/types";
import { LiteBox } from "../lite/LiteBox";
import LyfeFormField from "../LyfeFormField";
import draggableWindowStyles from "./draggable-window-styles";
import { GET_ALL_REFUNDS_OF_RECORD, SAVE_ADJUSTED_AMOUNT } from "./queries";
import { GetAllRefundsOfRecordQuery } from "./types/queries.generated";
import { EMPTY_VALUE_PLACEHOLDER } from "../../models/common/data";

enum AdjustedTypes {
	AMOUNT = "amount",
	RATE = "rate",
}

enum DiscountTypes {
	EXISTING = "existing",
	POTENTIAL = "potential",
	EXISTING_AND_POTENTIAL = "existing and potential",
}

export interface DataForAdjustmentProps {
	refundId: string;
	isInReview: boolean;
	isCreator: boolean | undefined;
}

export interface DraggableWindowRootProps {
	open: boolean;
	close: (isComplete?: boolean) => void;
	currentlyAdjustedRefunds: Array<DataForAdjustmentProps> | null;
	setIsSaving?: Dispatch<SetStateAction<boolean>>;
	refetch?: () => void;
}

const getDisclaimer = (hasVariable: boolean, hasOtherTransactions: boolean, recordTypeLocalizationKey: string, t: TrFun) => {
	if (!hasVariable && !hasOtherTransactions) {
		return null;
	}

	const getDiscountType = () => {
		if (hasVariable) {
			return hasOtherTransactions ? DiscountTypes.EXISTING_AND_POTENTIAL : DiscountTypes.POTENTIAL;
		}
		return DiscountTypes.EXISTING;
	};

	const discountType = t(getDiscountType());
	const recordType = t(recordTypeLocalizationKey);

	return (
		<Trans i18nKey={`transaction-adjustment-disclaimer${hasVariable && hasOtherTransactions ? "-multiple" : ""}`}>
			{{ discountType, recordType }}
		</Trans>
	);
};

const getGrossAmount = (dataPoint: Pick<FlexFinancialDataPoint, "basePrice" | "quantity">) => dataPoint.basePrice * dataPoint.quantity || 0;
const getPercentage = (amount: number, dataPoint: Pick<FlexFinancialDataPoint, "basePrice" | "quantity">) =>
	(100 * (amount / getGrossAmount(dataPoint))).toFixed(2);
const getActualAmount = (refund: Pick<Refund, "adjustedAmount" | "amount">) =>
	refund.adjustedAmount ? refund.adjustedAmount : refund.amount;

const DraggableWindowRoot: React.FC<DraggableWindowRootProps> = (props) => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const { currentlyAdjustedRefunds, open, close, setIsSaving, refetch } = props;
	const { currentAmountStyle, grossAmountStyle, colorGray, inputFiled } = draggableWindowStyles();
	const [state, setState] = useState({ adjustedAmount: "", adjustedRate: "" });
	const setPosition = useSetRecoilState(adjustmentWindowPosition);
	const position = useRecoilValue(adjustmentWindowPosition);
	const setSnackBar = useSetRecoilState(snackbarState);
	const apolloClient = useApolloClient();

	const { data: refundData, loading } = useDataQuery<GetAllRefundsOfRecordQuery>(GET_ALL_REFUNDS_OF_RECORD, {
		variables: {
			refundIds: currentlyAdjustedRefunds?.map((r) => r.refundId) ?? [],
		},
		skip: !currentlyAdjustedRefunds?.length,
	});

	const hasVariable = refundData?.refunds.some((refund) =>
		refund.sourceFinancialDataPoint.evaluates.financialDataPoints.some((fdp) => fdp.isLast && !fdp.isDelta && fdp.variable > 0)
	);
	const hasOtherTransactions = refundData?.refunds.some(
		(refund) => refund.sourceFinancialDataPoint.evaluates.financialDataPoints.filter((fdp) => fdp.sourceRefund).length > 1
	);

	const recordType = refundData?.refunds[0]?.sourceFinancialDataPoint.evaluates.type.localizationKey;

	const isSameAmount = useMemo(
		() =>
			refundData &&
			distinct(refundData.refunds.map((r) => r.adjustedAmount)).length === 1 &&
			distinct(refundData.refunds.map((r) => r.amount)).length === 1 &&
			distinct(refundData.refunds.map((r) => r.sourceFinancialDataPoint.basePrice * r.sourceFinancialDataPoint.quantity)).length ===
				1,
		[refundData]
	);

	const [adjust, { loading: adjusting }] = useDataMutation(SAVE_ADJUSTED_AMOUNT, {
		onCompleted: () => {
			refetch?.();
			setIsSaving && setIsSaving(false);
			close(true);
		},
		onError: (error) => {
			console.info("ERROR");
			setIsSaving && setIsSaving(false);
			close(false);
			setSnackBar({
				isOpen: true,
				message: error.message,
				type: SnackBarMessageType.Error,
				duration: 8000,
			});
			apolloClient.refetchQueries({ include: "active" });
			console.log(error);
		},
	});

	const initialValues = useMemo(
		() => ({
			adjustedAmount:
				isSameAmount && !loading && refundData?.refunds.length ? getActualAmount(refundData?.refunds[0] as Refund).toFixed(2) : "",
			// If it was adjusted, the initial is the adjusted value, else - original amount
			adjustedRate:
				isSameAmount && !loading && refundData?.refunds.length
					? getPercentage(getActualAmount(refundData?.refunds[0]), refundData?.refunds[0].sourceFinancialDataPoint)
					: "",
		}),
		[refundData, isSameAmount]
	);

	useEffect(() => {
		setState(initialValues);
	}, [initialValues, setState]);

	if (!currentlyAdjustedRefunds?.length) {
		return <></>;
	}

	const validationSchema = Yup.object().shape({
		adjustedAmountMessage: Yup.string().nullable().max(1200),
	});

	const buildAdjustmentChatMessage = (
		refund: Pick<Omit<Refund, "sourceFinancialDataPoint">, "id" | "adjustedAmount" | "amount"> & {
			sourceFinancialDataPoint: Pick<FlexFinancialDataPoint, "basePrice" | "quantity">;
		},
		adjustedAmountMessage: string,
		adjustedAmount: number
	) => {
		return `${t("transaction-adjusted-from-to", {
			transactionId: refund.id,
			from: `${getActualAmount(refund)} (${getPercentage(getActualAmount(refund), refund.sourceFinancialDataPoint)}%)`,
			to: `${adjustedAmount} (${getPercentage(adjustedAmount, refund.sourceFinancialDataPoint)}%)`,
		})}${adjustedAmountMessage ? " " + t("with-the-comment") + ': "' + adjustedAmountMessage + '".' : "."}`;
	};

	const isValidInput = (value: string) => {
		const regex = /^(?!.*\..*\.)[0-9]*([\.\,][0-9]{0,2})?$/;
		return regex.test(value);
	};

	const replaceComma = (input: string) => {
		return input.replace(",", ".");
	};

	const handleSubmit = (formData: any) => {
		setIsSaving && setIsSaving(true);

		adjust({
			variables: {
				input: refundData?.refunds.map((refund) => {
					const amount = parseFloat(replaceComma(state.adjustedAmount));
					const validAmount = !isNaN(amount) && amount > EPSILON ? amount : 0;
					const rate = replaceComma(state.adjustedRate);
					const adjustedAmount = validAmount
						? validAmount.toFixed(2)
						: ((parseFloat(rate) * getGrossAmount(refund.sourceFinancialDataPoint)) / 100).toFixed(2);
					const adjustedRate = validAmount
						? (100 * ((validAmount + Number.EPSILON) / getGrossAmount(refund.sourceFinancialDataPoint))).toFixed(2)
						: rate;
					const adjustedAmountMessage = buildAdjustmentChatMessage(
						refund,
						formData.adjustedAmountMessage,
						parseFloat(replaceComma(adjustedAmount))
					);

					return {
						refundId: refund.id,
						adjustedAmount,
						adjustedRate,
						adjustedAmountMessage,
						adjustmentComment: formData.adjustedAmountMessage,
					};
				}),
			},
		});
	};

	const EPSILON = 0.01;
	const minGrossAmount = Math.min(...(refundData?.refunds?.map((r) => getGrossAmount(r.sourceFinancialDataPoint)) ?? []));
	const maxGrossAmount = Math.max(0, ...(refundData?.refunds?.map((r) => getGrossAmount(r.sourceFinancialDataPoint)) ?? []));

	const handleValidateAmountOnBlur = () => {
		if (!isSameAmount) {
			if (
				parseFloat(replaceComma(state.adjustedAmount)) &&
				maxGrossAmount &&
				(parseFloat(replaceComma(state.adjustedAmount)) / maxGrossAmount) * 100 < EPSILON
			) {
				setState({ ...initialValues });
				return;
			}

			return;
		}

		const number = parseFloat(replaceComma(state.adjustedAmount));
		const isValidAmount = isValidInput(state.adjustedAmount);

		if (isValidAmount) {
			if (number >= EPSILON && number <= minGrossAmount) {
				setState({
					...state,
					adjustedAmount: parseFloat(replaceComma(state.adjustedAmount)).toFixed(2),
					adjustedRate: parseFloat(replaceComma(state.adjustedRate)).toFixed(2),
				});
				return;
			}
		}

		setState({ ...initialValues });
	};

	function handleChangeValues(e: any, type: AdjustedTypes) {
		if (e.target.value === "") {
			setState({
				...state,
				adjustedAmount: "",
				adjustedRate: "",
			});
			return;
		}

		const isValidAmount = isValidInput(e.target?.value);

		if (isValidAmount) {
			const valueWithoutComma = replaceComma(e.target.value);
			const getNumbers = () => {
				if (!isSameAmount) {
					return { amountAsNumber: 0, newValueAsString: "" };
				}

				if (type === AdjustedTypes.RATE) {
					const newValueAsString = ((+valueWithoutComma * minGrossAmount) / 100).toFixed(2);

					return { amountAsNumber: parseFloat(replaceComma(newValueAsString)), newValueAsString };
				}

				const rateValueAsString = (100 * ((+valueWithoutComma + Number.EPSILON) / minGrossAmount)).toFixed(2);

				return { amountAsNumber: parseFloat(valueWithoutComma), newValueAsString: rateValueAsString };
			};
			const { amountAsNumber, newValueAsString } = getNumbers();

			// edge case for 00.00; 0,00;
			const newTargetValue = valueWithoutComma.split(".");

			if (
				!isNaN(amountAsNumber) &&
				(amountAsNumber < 0 || amountAsNumber > minGrossAmount || (+newTargetValue[0] === 0 && newTargetValue[1] === "00"))
			) {
				return;
			}

			if (type === AdjustedTypes.RATE) {
				const maxPercentage = 100;
				if (parseFloat(valueWithoutComma) > maxPercentage) {
					return;
				}

				setState({
					...state,
					adjustedAmount: newValueAsString === "NaN" ? "0" : newValueAsString,
					adjustedRate: e.target?.value,
				});

				return;
			}

			setState({
				...state,
				adjustedAmount: e.target?.value,
				adjustedRate: newValueAsString === "NaN" ? "0" : newValueAsString,
			});
		}
	}

	const getInfoMessage = () => {
		if (!currentlyAdjustedRefunds[0].isInReview) {
			return undefined;
		}

		return currentlyAdjustedRefunds[0].isCreator
			? "transaction-adjustment-helptext-sender"
			: "transaction-adjustment-helptext-reviewer";
	};

	const areNumbersValid = () => {
		if (!isSameAmount && parseFloat(replaceComma(state.adjustedRate))) {
			return true;
		}

		const difference = Math.round(100 * Math.abs(Number(state.adjustedAmount) - Number(initialValues.adjustedAmount))) / 100;

		return difference >= EPSILON && Number(state.adjustedAmount) >= EPSILON && Number(state.adjustedAmount) <= minGrossAmount;
	};

	const isGrossAmountExceeded = !!state.adjustedAmount && parseFloat(replaceComma(state.adjustedAmount)) > minGrossAmount;

	return (
		<div>
			<Prompt when={open} message={t("changes-made-not-saved")} />
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={(values) => handleSubmit(values)}
				validateOnChange
				validateOnMount
				validateOnBlur
			>
				{({ submitForm, isValid, handleChange }) => {
					return (
						<NonModal
							open={open}
							handleClose={() => close()}
							titleKey={"document-transaction-adjust"}
							confirmButton={{
								loading: adjusting || loading,
								onConfirm: submitForm,
								disabled: loading || adjusting || !isValid || !areNumbersValid(),
								isGrossAmountExceeded,
							}}
							cancelButton={{ disabled: loading || adjusting }}
							tertiaryButton={{
								disabled: loading || adjusting || !isSameAmount,
								areInconsistentAmounts: !isSameAmount,
								onClick: () => {
									setState({
										...state,
										adjustedAmount: refundData!.refunds[0].amount.toFixed(2),
										adjustedRate: getPercentage(
											refundData!.refunds[0].amount,
											refundData!.refunds[0].sourceFinancialDataPoint
										),
									});
								},
							}}
							infoMessage={getInfoMessage()}
							infoPermanent={t("transaction-adjustment-note")}
							position={position}
							setPosition={setPosition}
						>
							<>
								{/* If a variable exists or there is more then one refund for a record, disclaimer is shown */}
								{!loading && refundData && recordType && (hasVariable || hasOtherTransactions) && (
									<Box mb={2}>
										<Typography variant="body2" style={{ color: palette.tableText.main }}>
											{getDisclaimer(!!hasVariable, !!hasOtherTransactions, recordType, t)}
										</Typography>
									</Box>
								)}
								{currentlyAdjustedRefunds.length === 1 && (
									<LiteBox
										display="inline-flex"
										justifyContent="flex-start"
										marginBottom={2}
										className={currentAmountStyle}
									>
										<LiteBox className={grossAmountStyle} paddingLeft={0.8} paddingRight={3}>
											<Typography variant="caption" color="textSecondary">
												{t("gross-amount")}
											</Typography>
											<Typography variant="body1">
												{minGrossAmount < Number.POSITIVE_INFINITY
													? renderCurrency(minGrossAmount, refundData?.refunds[0]?.currency, 2, 2)
													: EMPTY_VALUE_PLACEHOLDER}
											</Typography>
										</LiteBox>
										<LiteBox paddingLeft={3} paddingRight={1}>
											<Typography variant="caption" color="textSecondary">
												{t("quantity")}
											</Typography>
											<Typography variant="body1">
												{refundData?.refunds[0]?.sourceFinancialDataPoint.quantity ?? EMPTY_VALUE_PLACEHOLDER}
											</Typography>
										</LiteBox>
									</LiteBox>
								)}
								<Box display={"flex"} mb={2} alignItems={"center"}>
									<Box flexBasis={"50%"}>
										<TextField
											fullWidth
											variant="outlined"
											classes={{ root: inputFiled }}
											label={t("transaction-adjusted-amount")}
											required={true}
											disabled={loading}
											InputProps={{
												endAdornment: <span className={colorGray}>{refundData?.refunds[0]?.currency}</span>,
												inputProps: {
													max: getGrossAmount,
												},
											}}
											InputLabelProps={{
												classes: {
													root: colorGray,
												},
												shrink: true,
											}}
											value={isEmptyValue(state.adjustedAmount) ? "" : state.adjustedAmount}
											onChange={(e) => handleChangeValues(e, AdjustedTypes.AMOUNT)}
											onBlur={handleValidateAmountOnBlur}
										/>
									</Box>
									<LiteBox mx={3}>
										<CompareArrowsIcon htmlColor="#667481" />
									</LiteBox>
									<Box style={{ flexBasis: "50%" }}>
										<TextField
											fullWidth
											variant="outlined"
											classes={{ root: inputFiled }}
											label={t("transaction-adjusted-rate")}
											required={true}
											disabled={loading}
											InputProps={{
												endAdornment: <span className={colorGray}>%</span>,
												inputProps: {
													max: getGrossAmount,
												},
											}}
											InputLabelProps={{
												classes: {
													root: colorGray,
												},
												shrink: true,
											}}
											value={isEmptyValue(state.adjustedRate) ? "" : state.adjustedRate}
											onChange={(e) => handleChangeValues(e, AdjustedTypes.RATE)}
											onBlur={handleValidateAmountOnBlur}
										/>
									</Box>
								</Box>
								<LiteBox width="100%">
									<LyfeFormField
										id={"adjustedAmountMessage"}
										name="adjustedAmountMessage"
										multiline
										minRows={3}
										fullWidth
										label={t("comment")}
										InputLabelProps={{
											classes: {
												root: colorGray,
											},
										}}
										placeholder={`${t("type-your-comment-here")}...`}
										disabled={loading}
										onChange={(e) => {
											handleChange(e);
										}}
									/>
								</LiteBox>
							</>
						</NonModal>
					);
				}}
			</Formik>
		</div>
	);
};

export default DraggableWindowRoot;
